import React, { useState, useEffect, useCallback } from "react";
import { Alert } from "../types";
import { useAlerts, useDeviceUuids } from "./useAppAlerts";
import { getAlertTimestamp } from "../utils/helpers";

const PAGE_SIZE = 10;

export const useInfiniteAlerts = (options: {
  types?: ("detection" | "maintenance")[];
  msg_types?: string[];
  pollingInterval?: number;
  /** Superuser panel only - see the note on useTrapsiteIds. */
  org?: string;
}) => {
  const { types, msg_types, pollingInterval, org } = options;
  const [page, setPage] = useState(0);
  const [allAlerts, setAllAlerts] = useState<Alert[]>([]);

  const { deviceUuids } = useDeviceUuids({ org });
  const deviceUuidsKey = deviceUuids.join(",");
  const msgTypesKey = (msg_types ?? []).join(",");

  const {
    data: alerts,
    isLoading: isAlertsLoading,
    isFetching,
  } = useAlerts({
    page,
    pageSize: PAGE_SIZE,
    types,
    msg_types,
    org,
  });

  // Background polling for page 0 to keep the list fresh.
  // RTK Query deduplicates requests with the same cache key, so when page === 0
  // this subscriber shares the same in-flight request as the main query above.
  const { data: page0PollingData } = useAlerts({
    page: 0,
    pageSize: PAGE_SIZE,
    types,
    msg_types,
    pollingInterval,
    skip: !pollingInterval,
    org,
  });

  // Reset alerts when the device list changes
  useEffect(() => {
    setPage(0);
    setAllAlerts([]);
  }, [deviceUuidsKey, msgTypesKey]);

  // Triggered by new data arriving, not by page increments — page is intentionally
  // excluded from deps so that incrementing page doesn't itself cause a re-append.
  useEffect(() => {
    if (!alerts?.items) return;
    if (page === 0) {
      setAllAlerts(alerts.items);
    } else {
      setAllAlerts((prev) => {
        const existingIds = new Set(prev.map((a) => a._id));
        const newItems = alerts.items.filter(
          (item) => !existingIds.has(item._id),
        );
        const statusMap = new Map(alerts.items.map((a) => [a._id, a.status]));
        const updated = prev.map((a) => {
          const newStatus = statusMap.get(a._id);
          return newStatus !== undefined && newStatus !== a.status
            ? { ...a, status: newStatus }
            : a;
        });
        return [...updated, ...newItems].sort(
          (a, b) =>
            new Date(getAlertTimestamp(b)).getTime() -
            new Date(getAlertTimestamp(a)).getTime(),
        );
      });
    }
  }, [alerts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle background polling for page 0. Skip when page === 0 since the main
  // query effect above already handles that case via the shared cache entry.
  useEffect(() => {
    if (page === 0) return;
    if (!page0PollingData?.items || page0PollingData.items.length === 0) return;

    setAllAlerts((prev) => {
      const existingIds = new Set(prev.map((a) => a._id));
      const newItems = page0PollingData.items.filter(
        (item) => !existingIds.has(item._id),
      );

      if (newItems.length === 0) return prev;

      return [...newItems, ...prev].sort(
        (a, b) =>
          new Date(getAlertTimestamp(b)).getTime() -
          new Date(getAlertTimestamp(a)).getTime(),
      );
    });
  }, [page0PollingData]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalCount = alerts?.count ?? 0;
  const hasMore = allAlerts.length < totalCount;

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      if (isFetching || !hasMore) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
        setPage((prev) => prev + 1);
      }
    },
    [isFetching, hasMore],
  );

  return {
    allAlerts,
    isLoading: isAlertsLoading && page === 0,
    isFetchingMore: isFetching && page > 0,
    handleScroll,
    totalCount,
  };
};
